import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield, Activity, AlertTriangle, MapPin, Menu, LogOut, Zap, BarChart3, ListTodo, FileDown, Battery, TrendingDown, Eye, Server, Terminal, Brain, Bug, Clipboard, Lock, Wifi, ChevronRight, Download, RefreshCw, Network, Monitor, Radio, Search, Globe, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Severity color helpers
const severityColor = (s) => {
  const m = { Critical: 'bg-red-600 text-white', High: 'bg-orange-500 text-white', Medium: 'bg-amber-500 text-black', Low: 'bg-blue-500 text-white' };
  return m[s] || 'bg-gray-400 text-white';
};
const severityBorder = (s) => {
  const m = { Critical: 'border-red-500', High: 'border-orange-500', Medium: 'border-amber-500', Low: 'border-blue-500' };
  return m[s] || 'border-gray-300';
};
const statusColor = (s) => {
  const m = { Detected: 'bg-red-100 text-red-700', Triaging: 'bg-amber-100 text-amber-700', Contained: 'bg-blue-100 text-blue-700', Remediating: 'bg-purple-100 text-purple-700', Closed: 'bg-green-100 text-green-700', Open: 'bg-red-100 text-red-700' };
  return m[s] || 'bg-gray-100 text-gray-700';
};

const CyberSecurityPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');

  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [otEvents, setOtEvents] = useState([]);
  const [otStats, setOtStats] = useState(null);
  const [devices, setDevices] = useState([]);
  const [controlLedger, setControlLedger] = useState([]);
  const [accessLogs, setAccessLogs] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [vulnerabilities, setVulnerabilities] = useState(null);
  const [exposureScores, setExposureScores] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [threatMap, setThreatMap] = useState(null);
  // Filters
  const [alertCategoryFilter, setAlertCategoryFilter] = useState('all');
  const [alertSeverityFilter, setAlertSeverityFilter] = useState('all');

  // Live Threat Ticker state
  const [tickerAlerts, setTickerAlerts] = useState([]);
  const [tickerMuted, setTickerMuted] = useState(false);
  const [tickerPaused, setTickerPaused] = useState(false);
  const [edgeFlash, setEdgeFlash] = useState(null); // 'Critical' | 'High' | null
  const prevAlertIdsRef = useRef(new Set());
  const audioCtxRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) { navigate('/login'); return; }
    setUser(JSON.parse(userData));
    loadAllData();
  }, []);

  const getConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const loadAllData = async () => {
    try {
      const config = getConfig();
      const [dashRes, alertsRes, otEventsRes, otStatsRes, devicesRes, ledgerRes, accessRes, anomRes, vulnRes, exposureRes, incRes, compRes, threatMapRes] = await Promise.all([
        axios.get(`${API}/cyber/dashboard`, config).catch(() => ({ data: null })),
        axios.get(`${API}/cyber/alerts?status=Open`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/cyber/ot-network/events`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/cyber/ot-network/stats`, config).catch(() => ({ data: null })),
        axios.get(`${API}/cyber/devices`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/cyber/control-ledger`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/cyber/access-logs`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/cyber/anomalies`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/cyber/vulnerabilities`, config).catch(() => ({ data: null })),
        axios.get(`${API}/cyber/vulnerabilities/exposure`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/cyber/incidents`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/cyber/compliance`, config).catch(() => ({ data: null })),
        axios.get(`${API}/cyber/threat-map`, config).catch(() => ({ data: null })),
      ]);
      setDashboard(dashRes.data);
      setAlerts(alertsRes.data || []);
      setOtEvents(otEventsRes.data || []);
      setOtStats(otStatsRes.data);
      setDevices(devicesRes.data || []);
      setControlLedger(ledgerRes.data || []);
      setAccessLogs(accessRes.data || []);
      setAnomalies(anomRes.data || []);
      setVulnerabilities(vulnRes.data);
      setExposureScores(exposureRes.data || []);
      setIncidents(incRes.data || []);
      setCompliance(compRes.data);
      setThreatMap(threatMapRes.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.clear(); navigate('/login'); }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/cyber/generate-all`, {}, getConfig());
      toast.success('Cyber security data generated');
      await loadAllData();
    } catch (err) {
      toast.error('Failed to generate data');
    } finally {
      setGenerating(false);
    }
  };

  const handleLogout = () => { localStorage.clear(); navigate('/'); toast.success('Logged out'); };

  // ---- Live Threat Ticker: Sound notification ----
  const playAlertSound = useCallback((severity) => {
    if (tickerMuted) return;
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = severity === 'Critical' ? 'square' : 'sine';
      const baseFreq = severity === 'Critical' ? 880 : 660;
      osc.frequency.setValueAtTime(baseFreq, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.5, ctx.currentTime + 0.3);
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.4);
    } catch (_) { /* audio blocked by browser */ }
  }, [tickerMuted]);

  // ---- Live Threat Ticker: Polling every 30s ----
  useEffect(() => {
    if (!dashboard) return;
    const pollTicker = async () => {
      try {
        const res = await axios.get(`${API}/cyber/alerts?status=Open`, getConfig());
        const freshAlerts = res.data || [];
        setTickerAlerts(freshAlerts.slice(0, 20));

        // Detect new alerts
        const currentIds = new Set(freshAlerts.map(a => a.id));
        const prevIds = prevAlertIdsRef.current;
        if (prevIds.size > 0) {
          const newAlerts = freshAlerts.filter(a => !prevIds.has(a.id));
          if (newAlerts.length > 0) {
            const maxSeverity = newAlerts.some(a => a.severity === 'Critical') ? 'Critical' : newAlerts.some(a => a.severity === 'High') ? 'High' : null;
            if (maxSeverity) {
              playAlertSound(maxSeverity);
              setEdgeFlash(maxSeverity);
              setTimeout(() => setEdgeFlash(null), 2000);
            }
          }
        }
        prevAlertIdsRef.current = currentIds;
      } catch (_) { /* silent fail */ }
    };
    // Initial ticker load
    setTickerAlerts(alerts.slice(0, 20));
    prevAlertIdsRef.current = new Set(alerts.map(a => a.id));

    const interval = setInterval(pollTicker, 30000);
    return () => clearInterval(interval);
  }, [dashboard, alerts, playAlertSound]);

  const filteredAlerts = useMemo(() => {
    return alerts.filter(a => {
      if (alertCategoryFilter !== 'all' && a.category !== alertCategoryFilter) return false;
      if (alertSeverityFilter !== 'all' && a.severity !== alertSeverityFilter) return false;
      return true;
    });
  }, [alerts, alertCategoryFilter, alertSeverityFilter]);

  const fmtDate = (iso) => { if (!iso) return '-'; return iso.substring(0, 16).replace('T', ' '); };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-gray-600">Loading Cyber-Physical Security...</div></div>;

  const hasData = dashboard && dashboard.total_alerts > 0;

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="cyber-security-page">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-gray-200 transition-all duration-300 flex flex-col`}>
        <div className="p-6 border-b border-gray-200 flex items-center justify-center">
          <img src="/sodiumq-logo.png" alt="SodiumQ" className={`${sidebarOpen ? 'h-10' : 'h-8'} w-auto transition-all duration-300`} />
        </div>
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <Button variant="ghost" className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100" onClick={() => navigate('/dashboard')} data-testid="nav-dashboard">
            <BarChart3 className="w-5 h-5" />{sidebarOpen && <span className="ml-3">Dashboard</span>}
          </Button>
          <Button variant="ghost" className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100" onClick={() => navigate('/map')} data-testid="nav-map">
            <MapPin className="w-5 h-5" />{sidebarOpen && <span className="ml-3">Map View</span>}
          </Button>
          <Button variant="ghost" className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100" onClick={() => navigate('/alerts')} data-testid="nav-alerts">
            <AlertTriangle className="w-5 h-5" />{sidebarOpen && <span className="ml-3">Alerts</span>}
          </Button>
          <Button variant="ghost" className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100" onClick={() => navigate('/sca-alerts')} data-testid="nav-sca-alerts">
            <Shield className="w-5 h-5" />{sidebarOpen && <span className="ml-3">SCA Alerts</span>}
          </Button>
          <Button variant="ghost" className="w-full justify-start text-emerald-600 bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-700" onClick={() => navigate('/cyber-security')} data-testid="nav-cyber-security">
            <Lock className="w-5 h-5" />{sidebarOpen && <span className="ml-3">Cyber-Physical Security</span>}
          </Button>
          <Button variant="ghost" className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100" onClick={() => navigate('/tasks')} data-testid="nav-tasks">
            <ListTodo className="w-5 h-5" />{sidebarOpen && <span className="ml-3">Tasks</span>}
          </Button>
          <Button variant="ghost" className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100" onClick={() => navigate('/reports/soh')} data-testid="nav-soh-report">
            <FileDown className="w-5 h-5" />{sidebarOpen && <span className="ml-3">SOH Report</span>}
          </Button>
          <Button variant="ghost" className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100" onClick={() => navigate('/reports/soc')} data-testid="nav-soc-report">
            <Battery className="w-5 h-5" />{sidebarOpen && <span className="ml-3">SOC Report</span>}
          </Button>
          <Button variant="ghost" className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100" onClick={() => navigate('/reports/degradation')} data-testid="nav-degradation-report">
            <TrendingDown className="w-5 h-5" />{sidebarOpen && <span className="ml-3">Degradation Report</span>}
          </Button>
        </nav>
        <div className="p-4 border-t border-gray-200">
          <Button variant="ghost" className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-5 h-5" />{sidebarOpen && <span className="ml-3">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="toggle-sidebar-btn"><Menu className="w-6 h-6" /></Button>
              <div>
                <h1 className="text-3xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>Cyber-Physical Security</h1>
                <p className="text-gray-500 text-sm">OT Network Monitoring &middot; Passive IDS &middot; Threat Intelligence</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={loadAllData} data-testid="refresh-cyber-btn"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
              {user?.role === 'admin' && (
                <Button size="sm" onClick={handleGenerate} disabled={generating} data-testid="generate-cyber-data-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Zap className="w-4 h-4 mr-2" />{generating ? 'Generating...' : 'Generate Test Data'}
                </Button>
              )}
            </div>
          </div>

          {/* ---- Live Threat Feed Ticker ---- */}
          {hasData && tickerAlerts.length > 0 && (
            <div className="mb-5 relative" data-testid="threat-ticker-container">
              {/* Edge flash overlay */}
              {edgeFlash && (
                <div className={`fixed inset-0 pointer-events-none z-50 ${edgeFlash === 'Critical' ? 'animate-[edgeFlashRed_2s_ease-out_forwards]' : 'animate-[edgeFlashAmber_2s_ease-out_forwards]'}`} data-testid="edge-flash" style={{
                  boxShadow: edgeFlash === 'Critical'
                    ? 'inset 0 0 80px rgba(239,68,68,0.4), inset 0 0 160px rgba(239,68,68,0.15)'
                    : 'inset 0 0 60px rgba(245,158,11,0.3), inset 0 0 120px rgba(245,158,11,0.1)'
                }} />
              )}
              <div className="bg-[#0c1821] rounded-xl border border-[#1e3a4f] overflow-hidden">
                {/* Ticker header bar */}
                <div className="flex items-center justify-between px-4 py-1.5 bg-[#0f2233] border-b border-[#1e3a4f]">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
                    </span>
                    <span className="text-[11px] font-semibold text-gray-300 uppercase tracking-widest">Live Threat Feed</span>
                    <Badge className="bg-red-900/50 text-red-300 text-[9px] border border-red-700/40">{tickerAlerts.length} active</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-500">Auto-refresh 30s</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-white hover:bg-[#1e3a4f]" onClick={() => setTickerMuted(!tickerMuted)} data-testid="ticker-mute-btn">
                      {tickerMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
                {/* Scrolling ticker */}
                <div
                  className="relative overflow-hidden"
                  style={{ height: '40px' }}
                  onMouseEnter={() => setTickerPaused(true)}
                  onMouseLeave={() => setTickerPaused(false)}
                  data-testid="threat-ticker-scroll"
                >
                  <div
                    className="flex items-center gap-6 absolute whitespace-nowrap"
                    style={{
                      animation: `tickerScroll ${Math.max(30, tickerAlerts.length * 4)}s linear infinite`,
                      animationPlayState: tickerPaused ? 'paused' : 'running',
                      paddingLeft: '100%'
                    }}
                  >
                    {tickerAlerts.map((alert, idx) => {
                      const sevBg = { Critical: 'bg-red-500', High: 'bg-orange-500', Medium: 'bg-amber-500', Low: 'bg-blue-500' }[alert.severity] || 'bg-gray-500';
                      const sevText = { Critical: 'text-red-400', High: 'text-orange-400', Medium: 'text-amber-400', Low: 'text-blue-400' }[alert.severity] || 'text-gray-400';
                      return (
                        <div
                          key={alert.id || idx}
                          className="inline-flex items-center gap-2.5 cursor-pointer group"
                          onClick={() => { setActiveTab('ot-ids'); setAlertCategoryFilter(alert.category); }}
                          data-testid={`ticker-alert-${idx}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${sevBg} flex-shrink-0`} />
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${sevText}`}>{alert.severity}</span>
                          <span className="text-[11px] text-gray-300 group-hover:text-white transition-colors">{alert.title}</span>
                          <span className="text-[10px] text-gray-600">{alert.category}</span>
                          <span className="text-[10px] text-gray-700">{alert.timestamp ? alert.timestamp.substring(11, 16) : ''}</span>
                          <span className="text-gray-700 mx-1">|</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!hasData ? (
            <Card className="bg-white border-dashed border-2 border-gray-300">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Lock className="w-16 h-16 text-gray-300 mb-4" />
                <h2 className="text-xl font-semibold text-gray-600 mb-2">No Cyber Security Data</h2>
                <p className="text-gray-400 mb-6">Click "Generate Test Data" to populate the cyber-physical security module with simulated OT network telemetry, device inventory, and threat data.</p>
                {user?.role === 'admin' && (
                  <Button onClick={handleGenerate} disabled={generating} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="generate-empty-state-btn">
                    <Zap className="w-4 h-4 mr-2" />{generating ? 'Generating...' : 'Generate Test Data'}
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="bg-white border border-gray-200 p-1 mb-6 flex-wrap h-auto gap-1" data-testid="cyber-tabs">
                <TabsTrigger value="dashboard" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-xs px-3 py-1.5" data-testid="tab-dashboard"><Eye className="w-3.5 h-3.5 mr-1.5" />Dashboard</TabsTrigger>
                <TabsTrigger value="ot-ids" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-xs px-3 py-1.5" data-testid="tab-ot-ids"><Network className="w-3.5 h-3.5 mr-1.5" />OT Network IDS</TabsTrigger>
                <TabsTrigger value="devices" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-xs px-3 py-1.5" data-testid="tab-devices"><Server className="w-3.5 h-3.5 mr-1.5" />Device Integrity</TabsTrigger>
                <TabsTrigger value="command-access" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-xs px-3 py-1.5" data-testid="tab-command-access"><Terminal className="w-3.5 h-3.5 mr-1.5" />Command & Access</TabsTrigger>
                <TabsTrigger value="anomalies" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-xs px-3 py-1.5" data-testid="tab-anomalies"><Brain className="w-3.5 h-3.5 mr-1.5" />Anomaly Detection</TabsTrigger>
                <TabsTrigger value="threat-intel" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-xs px-3 py-1.5" data-testid="tab-threat-intel"><Bug className="w-3.5 h-3.5 mr-1.5" />Threat Intel</TabsTrigger>
                <TabsTrigger value="incidents" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-xs px-3 py-1.5" data-testid="tab-incidents"><Clipboard className="w-3.5 h-3.5 mr-1.5" />Incidents</TabsTrigger>
                <TabsTrigger value="threat-map" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-xs px-3 py-1.5" data-testid="tab-threat-map"><Globe className="w-3.5 h-3.5 mr-1.5" />Threat Map</TabsTrigger>
                <TabsTrigger value="compliance" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-xs px-3 py-1.5" data-testid="tab-compliance"><Shield className="w-3.5 h-3.5 mr-1.5" />Compliance</TabsTrigger>
              </TabsList>

              {/* ============ DASHBOARD TAB ============ */}
              <TabsContent value="dashboard">
                <DashboardTab dashboard={dashboard} alerts={alerts} incidents={incidents} exposureScores={exposureScores} setActiveTab={setActiveTab} fmtDate={fmtDate} />
              </TabsContent>

              {/* ============ OT NETWORK IDS TAB ============ */}
              <TabsContent value="ot-ids">
                <OTNetworkTab otEvents={otEvents} otStats={otStats} fmtDate={fmtDate} />
              </TabsContent>

              {/* ============ DEVICE INTEGRITY TAB ============ */}
              <TabsContent value="devices">
                <DeviceIntegrityTab devices={devices} fmtDate={fmtDate} />
              </TabsContent>

              {/* ============ COMMAND & ACCESS TAB ============ */}
              <TabsContent value="command-access">
                <CommandAccessTab controlLedger={controlLedger} accessLogs={accessLogs} fmtDate={fmtDate} />
              </TabsContent>

              {/* ============ ANOMALY DETECTION TAB ============ */}
              <TabsContent value="anomalies">
                <AnomalyTab anomalies={anomalies} fmtDate={fmtDate} />
              </TabsContent>

              {/* ============ THREAT INTEL TAB ============ */}
              <TabsContent value="threat-intel">
                <ThreatIntelTab vulnerabilities={vulnerabilities} exposureScores={exposureScores} />
              </TabsContent>

              {/* ============ INCIDENTS TAB ============ */}
              <TabsContent value="incidents">
                <IncidentsTab incidents={incidents} fmtDate={fmtDate} getConfig={getConfig} onRefresh={loadAllData} />
              </TabsContent>

              {/* ============ THREAT MAP TAB ============ */}
              <TabsContent value="threat-map">
                <ThreatMapTab threatMap={threatMap} fmtDate={fmtDate} setActiveTab={setActiveTab} />
              </TabsContent>

              {/* ============ COMPLIANCE TAB ============ */}
              <TabsContent value="compliance">
                <ComplianceTab compliance={compliance} />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>
    </div>
  );
};

// ============ SUB-COMPONENTS ============

const StatCard = ({ title, value, sub, icon: Icon, color = 'emerald', onClick, testId }) => (
  <Card className={`bg-white border-gray-200 hover:shadow-md transition-all ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick} data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">{title}</p>
          <p className={`text-2xl font-bold text-${color}-600 mt-1`}>{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
        {Icon && <Icon className={`w-8 h-8 text-${color}-400 opacity-60`} />}
      </div>
    </CardContent>
  </Card>
);

// ---- Dashboard Tab ----
const DashboardTab = ({ dashboard, alerts, incidents, exposureScores, setActiveTab, fmtDate }) => {
  if (!dashboard) return null;
  const d = dashboard;
  return (
    <div className="space-y-6" data-testid="cyber-dashboard-content">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard title="Total Alerts" value={d.total_alerts} icon={AlertTriangle} color="red" onClick={() => setActiveTab('ot-ids')} testId="stat-total-alerts" />
        <StatCard title="Critical" value={d.critical_alerts} icon={Shield} color="red" testId="stat-critical" />
        <StatCard title="High" value={d.high_alerts} icon={AlertTriangle} color="orange" testId="stat-high" />
        <StatCard title="Active Incidents" value={d.active_incidents} icon={Clipboard} color="purple" onClick={() => setActiveTab('incidents')} testId="stat-incidents" />
        <StatCard title="Vulnerable Firmware" value={d.outdated_firmware_count} icon={Bug} color="amber" onClick={() => setActiveTab('threat-intel')} testId="stat-firmware" />
        <StatCard title="New Devices" value={d.new_devices_detected} icon={Monitor} color="blue" onClick={() => setActiveTab('devices')} testId="stat-new-devices" />
      </div>

      {/* OT Traffic + Category Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">OT Network Traffic</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-800">{d.total_ot_events}</p>
                <p className="text-xs text-gray-500">Total Events</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{d.read_ops}</p>
                <p className="text-xs text-gray-500">Read Ops</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{d.write_ops}</p>
                <p className="text-xs text-gray-500">Write Ops</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Anomalous:</span>
              <Badge className="bg-red-100 text-red-700">{d.anomalous_ot_events} events</Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Alert Categories</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(d.category_counts || {}).map(([cat, count]) => (
                <div key={cat} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{cat}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, (count / Math.max(1, d.total_alerts)) * 100)}%` }} />
                    </div>
                    <span className="text-sm font-medium text-gray-800 w-8 text-right">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Threat Scores + Recent Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Site Threat Scores</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {Object.entries(d.site_threat_scores || {})
                .sort((a, b) => b[1].threat_score - a[1].threat_score)
                .slice(0, 10)
                .map(([id, site]) => (
                  <div key={id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50">
                    <span className="text-sm text-gray-700 truncate max-w-[180px]">{site.name}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${site.threat_score > 50 ? 'bg-red-500' : site.threat_score > 20 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${site.threat_score}%` }} />
                      </div>
                      <span className={`text-xs font-bold w-8 text-right ${site.threat_score > 50 ? 'text-red-600' : site.threat_score > 20 ? 'text-amber-600' : 'text-green-600'}`}>{site.threat_score}</span>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Recent Alerts</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {(d.recent_alerts || []).map(alert => (
                <div key={alert.id} className={`p-2.5 rounded-lg border-l-4 bg-gray-50 ${severityBorder(alert.severity)}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-800 truncate max-w-[250px]">{alert.title}</span>
                    <Badge className={`${severityColor(alert.severity)} text-xs`}>{alert.severity}</Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{alert.category} &middot; {fmtDate(alert.timestamp)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Exposure Scores */}
      {exposureScores.length > 0 && (
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Site Exposure Scores (Top 10)</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 border-b"><th className="pb-2 font-medium">Site</th><th className="pb-2 font-medium">Exposure</th><th className="pb-2 font-medium">Devices</th><th className="pb-2 font-medium">Vulnerable</th><th className="pb-2 font-medium">Max CVSS</th></tr></thead>
                <tbody>
                  {exposureScores.slice(0, 10).map(s => (
                    <tr key={s.asset_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 text-gray-700">{s.asset_name}</td>
                      <td className="py-2"><Badge className={`${s.exposure_score > 50 ? 'bg-red-100 text-red-700' : s.exposure_score > 25 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{s.exposure_score.toFixed(1)}</Badge></td>
                      <td className="py-2 text-gray-600">{s.total_devices}</td>
                      <td className="py-2 text-gray-600">{s.vulnerable_devices}</td>
                      <td className="py-2"><span className={`font-medium ${s.max_cvss >= 9 ? 'text-red-600' : s.max_cvss >= 7 ? 'text-orange-600' : 'text-gray-600'}`}>{s.max_cvss.toFixed(1)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ---- OT Network IDS Tab ----
const OTNetworkTab = ({ otEvents, otStats, fmtDate }) => (
  <div className="space-y-6" data-testid="ot-ids-content">
    {otStats && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Total Events" value={otStats.total_events} icon={Radio} color="gray" testId="ot-total-events" />
        <StatCard title="Write Operations" value={otStats.write_count} icon={Terminal} color="red" testId="ot-write-ops" />
        <StatCard title="Read Operations" value={otStats.read_count} icon={Search} color="green" testId="ot-read-ops" />
        <StatCard title="Anomalous Events" value={otStats.anomalous_count} icon={AlertTriangle} color="red" testId="ot-anomalous" />
      </div>
    )}

    {/* Protocol Distribution */}
    {otStats && (
      <Card className="bg-white border-gray-200">
        <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Protocol Distribution</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(otStats.protocol_distribution || {}).map(([proto, count]) => (
              <div key={proto} className="text-center p-4 bg-gray-50 rounded-lg border">
                <p className="text-xs text-gray-500 mb-1">{proto}</p>
                <p className="text-xl font-bold text-gray-800">{count}</p>
                <div className="w-full h-1.5 bg-gray-200 rounded-full mt-2"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, (count / Math.max(1, otStats.total_events)) * 100)}%` }} /></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )}

    {/* Anomaly Breakdown */}
    {otStats?.anomaly_type_breakdown && Object.keys(otStats.anomaly_type_breakdown).length > 0 && (
      <Card className="bg-white border-gray-200">
        <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Detection Rules Triggered</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(otStats.anomaly_type_breakdown).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                <span className="text-sm text-red-700">{type}</span>
                <Badge className="bg-red-600 text-white">{count}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )}

    {/* Event Log */}
    <Card className="bg-white border-gray-200">
      <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">OT Network Event Log ({otEvents.length})</CardTitle></CardHeader>
      <CardContent>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm" data-testid="ot-events-table">
            <thead className="sticky top-0 bg-white"><tr className="text-left text-gray-500 border-b"><th className="pb-2 font-medium">Time</th><th className="pb-2 font-medium">Site</th><th className="pb-2 font-medium">Protocol</th><th className="pb-2 font-medium">Source</th><th className="pb-2 font-medium">Dest</th><th className="pb-2 font-medium">Op</th><th className="pb-2 font-medium">Status</th></tr></thead>
            <tbody>
              {otEvents.slice(0, 50).map(e => (
                <tr key={e.id} className={`border-b border-gray-100 ${e.is_anomalous ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                  <td className="py-1.5 text-xs text-gray-500">{fmtDate(e.timestamp)}</td>
                  <td className="py-1.5 text-gray-700 text-xs">{e.asset_name}</td>
                  <td className="py-1.5"><Badge variant="outline" className="text-xs">{e.protocol}</Badge></td>
                  <td className="py-1.5 text-xs font-mono text-gray-600">{e.source_ip}</td>
                  <td className="py-1.5 text-xs font-mono text-gray-600">{e.dest_ip}</td>
                  <td className="py-1.5"><Badge className={`text-xs ${e.operation === 'WRITE' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{e.operation}</Badge></td>
                  <td className="py-1.5">{e.is_anomalous ? <Badge className="bg-red-600 text-white text-xs">{e.anomaly_type}</Badge> : <span className="text-xs text-green-600">Normal</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  </div>
);

// ---- Device Integrity Tab ----
const DeviceIntegrityTab = ({ devices, fmtDate }) => {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? devices : filter === 'warning' ? devices.filter(d => d.status === 'Warning') : devices.filter(d => d.status === 'OK');
  const outdated = devices.filter(d => d.firmware_outdated).length;
  const drifted = devices.filter(d => d.time_drift_ms > 10).length;
  return (
    <div className="space-y-6" data-testid="devices-content">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Total Devices" value={devices.length} icon={Server} color="gray" testId="devices-total" />
        <StatCard title="Outdated Firmware" value={outdated} icon={Bug} color="red" testId="devices-outdated" />
        <StatCard title="Time Drift Issues" value={drifted} icon={Activity} color="amber" testId="devices-drift" />
        <StatCard title="Healthy" value={devices.length - outdated - drifted} icon={Shield} color="green" testId="devices-healthy" />
      </div>

      <Card className="bg-white border-gray-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold">Device Inventory ({filtered.length})</CardTitle>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="device-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Devices</SelectItem>
              <SelectItem value="warning">Warnings Only</SelectItem>
              <SelectItem value="ok">Healthy Only</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm" data-testid="devices-table">
              <thead className="sticky top-0 bg-white"><tr className="text-left text-gray-500 border-b"><th className="pb-2 font-medium">Site</th><th className="pb-2 font-medium">Type</th><th className="pb-2 font-medium">Vendor</th><th className="pb-2 font-medium">Firmware</th><th className="pb-2 font-medium">IP</th><th className="pb-2 font-medium">Uptime</th><th className="pb-2 font-medium">Drift</th><th className="pb-2 font-medium">Status</th></tr></thead>
              <tbody>
                {filtered.map(d => (
                  <tr key={d.id} className={`border-b border-gray-100 ${d.status === 'Warning' ? 'bg-amber-50' : 'hover:bg-gray-50'}`}>
                    <td className="py-1.5 text-xs text-gray-700">{d.asset_name}</td>
                    <td className="py-1.5 text-xs">{d.device_type}</td>
                    <td className="py-1.5 text-xs text-gray-600">{d.vendor}</td>
                    <td className="py-1.5 text-xs font-mono">{d.firmware_version}{d.firmware_outdated && <Badge className="bg-red-100 text-red-700 text-xs ml-1">OLD</Badge>}</td>
                    <td className="py-1.5 text-xs font-mono text-gray-600">{d.ip_address}</td>
                    <td className="py-1.5 text-xs text-gray-600">{d.uptime_days}d</td>
                    <td className="py-1.5 text-xs"><span className={d.time_drift_ms > 10 ? 'text-amber-600 font-medium' : 'text-gray-500'}>{d.time_drift_ms}ms</span></td>
                    <td className="py-1.5"><Badge className={d.status === 'OK' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{d.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ---- Command & Access Tab ----
const CommandAccessTab = ({ controlLedger, accessLogs, fmtDate }) => {
  const [view, setView] = useState('ledger');
  const suspiciousCommands = controlLedger.filter(e => e.is_suspicious).length;
  const failedLogins = accessLogs.filter(l => !l.success).length;
  const suspiciousAccess = accessLogs.filter(l => l.is_suspicious).length;
  return (
    <div className="space-y-6" data-testid="command-access-content">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Control Actions" value={controlLedger.length} icon={Terminal} color="gray" testId="cmd-total" />
        <StatCard title="Suspicious Commands" value={suspiciousCommands} icon={AlertTriangle} color="red" testId="cmd-suspicious" />
        <StatCard title="Remote Sessions" value={accessLogs.length} icon={Wifi} color="blue" testId="access-total" />
        <StatCard title="Failed Logins" value={failedLogins} icon={Lock} color="amber" testId="access-failed" />
      </div>

      <div className="flex gap-2 mb-2">
        <Button variant={view === 'ledger' ? 'default' : 'outline'} size="sm" onClick={() => setView('ledger')} data-testid="view-ledger-btn" className={view === 'ledger' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}>Control Action Ledger</Button>
        <Button variant={view === 'access' ? 'default' : 'outline'} size="sm" onClick={() => setView('access')} data-testid="view-access-btn" className={view === 'access' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}>Remote Access Logs</Button>
      </div>

      {view === 'ledger' ? (
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Control Action Ledger ({controlLedger.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm" data-testid="ledger-table">
                <thead className="sticky top-0 bg-white"><tr className="text-left text-gray-500 border-b"><th className="pb-2 font-medium">Time</th><th className="pb-2 font-medium">Site</th><th className="pb-2 font-medium">Command</th><th className="pb-2 font-medium">User</th><th className="pb-2 font-medium">Source IP</th><th className="pb-2 font-medium">Auth</th><th className="pb-2 font-medium">Risk</th></tr></thead>
                <tbody>
                  {controlLedger.map(e => (
                    <tr key={e.id} className={`border-b border-gray-100 ${e.is_suspicious ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                      <td className="py-1.5 text-xs text-gray-500">{fmtDate(e.timestamp)}</td>
                      <td className="py-1.5 text-xs text-gray-700">{e.asset_name}</td>
                      <td className="py-1.5 text-xs"><span className="font-medium">{e.command_type}</span><br /><span className="text-gray-400">{e.command_detail}</span></td>
                      <td className="py-1.5 text-xs font-mono text-gray-600">{e.user_account}</td>
                      <td className="py-1.5 text-xs font-mono text-gray-600">{e.source_ip}</td>
                      <td className="py-1.5"><Badge variant="outline" className="text-xs">{e.auth_method}</Badge></td>
                      <td className="py-1.5"><Badge className={e.risk_level === 'High' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>{e.risk_level}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Remote Access Logs ({accessLogs.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm" data-testid="access-table">
                <thead className="sticky top-0 bg-white"><tr className="text-left text-gray-500 border-b"><th className="pb-2 font-medium">Time</th><th className="pb-2 font-medium">Site</th><th className="pb-2 font-medium">Type</th><th className="pb-2 font-medium">User</th><th className="pb-2 font-medium">Source IP</th><th className="pb-2 font-medium">Duration</th><th className="pb-2 font-medium">Status</th></tr></thead>
                <tbody>
                  {accessLogs.map(l => (
                    <tr key={l.id} className={`border-b border-gray-100 ${l.is_suspicious ? 'bg-red-50' : !l.success ? 'bg-amber-50' : 'hover:bg-gray-50'}`}>
                      <td className="py-1.5 text-xs text-gray-500">{fmtDate(l.timestamp)}</td>
                      <td className="py-1.5 text-xs text-gray-700">{l.asset_name}</td>
                      <td className="py-1.5"><Badge variant="outline" className="text-xs">{l.access_type}</Badge></td>
                      <td className="py-1.5 text-xs font-mono text-gray-600">{l.user}</td>
                      <td className="py-1.5 text-xs font-mono text-gray-600">{l.source_ip}</td>
                      <td className="py-1.5 text-xs text-gray-600">{l.success ? `${l.duration_minutes}m` : '-'}</td>
                      <td className="py-1.5">{l.is_suspicious ? <Badge className="bg-red-600 text-white text-xs">Suspicious</Badge> : l.success ? <Badge className="bg-green-100 text-green-700 text-xs">OK</Badge> : <Badge className="bg-amber-100 text-amber-700 text-xs">Failed ({l.failed_attempts})</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ---- Anomaly Detection Tab ----
const AnomalyTab = ({ anomalies, fmtDate }) => {
  const correlated = anomalies.filter(a => a.correlated_ot_event).length;
  return (
    <div className="space-y-6" data-testid="anomalies-content">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Total Anomalies" value={anomalies.length} icon={Brain} color="purple" testId="anom-total" />
        <StatCard title="Correlated with OT" value={correlated} icon={AlertTriangle} color="red" testId="anom-correlated" />
        <StatCard title="Critical" value={anomalies.filter(a => a.severity === 'Critical').length} icon={Shield} color="red" testId="anom-critical" />
        <StatCard title="High Confidence" value={anomalies.filter(a => a.confidence > 80).length} icon={Eye} color="amber" testId="anom-high-conf" />
      </div>

      <div className="space-y-4">
        {anomalies.map(a => (
          <Card key={a.id} className={`bg-white border-l-4 ${severityBorder(a.severity)}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={severityColor(a.severity)}>{a.severity}</Badge>
                    <span className="text-sm font-semibold text-gray-800">{a.anomaly_type}</span>
                    <span className="text-xs text-gray-400">&middot; {a.asset_name}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{a.description}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-xs text-gray-500">Confidence: <span className="font-medium">{a.confidence}%</span></span>
                    <span className="text-xs text-gray-500">{fmtDate(a.timestamp)}</span>
                    {a.correlated_ot_event && <Badge className="bg-red-100 text-red-700 text-xs">OT Correlated</Badge>}
                    {a.maintenance_window_active && <Badge className="bg-blue-100 text-blue-700 text-xs">Maintenance Window</Badge>}
                  </div>
                  {a.leading_indicators && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {a.leading_indicators.map((ind, i) => <Badge key={i} variant="outline" className="text-xs">{ind}</Badge>)}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {anomalies.length === 0 && <p className="text-center text-gray-400 py-8">No behavioral anomalies detected.</p>}
      </div>
    </div>
  );
};

// ---- Threat Intel Tab ----
const ThreatIntelTab = ({ vulnerabilities, exposureScores }) => {
  const vulnDevices = vulnerabilities?.vulnerable_devices || [];
  const cveDb = vulnerabilities?.cve_database || [];
  return (
    <div className="space-y-6" data-testid="threat-intel-content">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Known CVEs" value={cveDb.length} icon={Bug} color="red" testId="cve-total" />
        <StatCard title="Vulnerable Devices" value={vulnDevices.length} icon={Server} color="amber" testId="vuln-devices" />
        <StatCard title="Critical CVEs" value={cveDb.filter(c => c.cvss >= 9).length} icon={AlertTriangle} color="red" testId="cve-critical" />
        <StatCard title="Sites At Risk" value={exposureScores.filter(s => s.exposure_score > 25).length} icon={Shield} color="amber" testId="sites-risk" />
      </div>

      {/* CVE Database */}
      <Card className="bg-white border-gray-200">
        <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">CVE Database</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {cveDb.map(cve => (
              <div key={cve.cve_id} className={`p-3 rounded-lg border-l-4 bg-gray-50 ${cve.cvss >= 9 ? 'border-red-500' : cve.cvss >= 7 ? 'border-orange-500' : cve.cvss >= 4 ? 'border-amber-500' : 'border-blue-500'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-bold text-gray-800">{cve.cve_id}</span>
                    <Badge className={`${cve.cvss >= 9 ? 'bg-red-600' : cve.cvss >= 7 ? 'bg-orange-500' : cve.cvss >= 4 ? 'bg-amber-500' : 'bg-blue-500'} text-white text-xs`}>CVSS {cve.cvss}</Badge>
                  </div>
                  <span className="text-xs text-gray-400">{cve.published}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{cve.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Vulnerable Devices */}
      {vulnDevices.length > 0 && (
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Vulnerable Devices ({vulnDevices.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="vuln-devices-table">
                <thead><tr className="text-left text-gray-500 border-b"><th className="pb-2 font-medium">Site</th><th className="pb-2 font-medium">Device</th><th className="pb-2 font-medium">Firmware</th><th className="pb-2 font-medium">CVEs</th><th className="pb-2 font-medium">Max CVSS</th><th className="pb-2 font-medium">Action</th></tr></thead>
                <tbody>
                  {vulnDevices.map(d => (
                    <tr key={d.device_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 text-xs text-gray-700">{d.asset_name}</td>
                      <td className="py-2 text-xs">{d.device_type} ({d.vendor})</td>
                      <td className="py-2 text-xs font-mono">{d.firmware_version}</td>
                      <td className="py-2"><div className="flex flex-wrap gap-1">{d.cves.map(c => <Badge key={c.cve_id} variant="outline" className="text-xs">{c.cve_id}</Badge>)}</div></td>
                      <td className="py-2"><Badge className={`${d.max_cvss >= 9 ? 'bg-red-600' : d.max_cvss >= 7 ? 'bg-orange-500' : 'bg-amber-500'} text-white text-xs`}>{d.max_cvss}</Badge></td>
                      <td className="py-2 text-xs text-emerald-600 font-medium">{d.remediation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Exposure Scores */}
      {exposureScores.length > 0 && (
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Site Exposure Scores</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {exposureScores.map(s => (
                <div key={s.asset_id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700 w-48 truncate">{s.asset_name}</span>
                  <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${s.exposure_score > 50 ? 'bg-red-500' : s.exposure_score > 25 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${s.exposure_score}%` }} />
                  </div>
                  <span className={`text-sm font-bold w-12 text-right ${s.exposure_score > 50 ? 'text-red-600' : s.exposure_score > 25 ? 'text-amber-600' : 'text-green-600'}`}>{s.exposure_score.toFixed(1)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ---- Incidents Tab ----
const IncidentsTab = ({ incidents, fmtDate, getConfig, onRefresh }) => {
  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/cyber/incidents/${id}/status?new_status=${newStatus}`, {}, getConfig());
      toast.success(`Incident updated to ${newStatus}`);
      onRefresh();
    } catch { toast.error('Failed to update incident'); }
  };

  return (
    <div className="space-y-6" data-testid="incidents-content">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {['Detected', 'Triaging', 'Contained', 'Remediating', 'Closed'].map(s => (
          <StatCard key={s} title={s} value={incidents.filter(i => i.status === s).length} color={s === 'Closed' ? 'green' : s === 'Detected' ? 'red' : 'amber'} testId={`inc-${s.toLowerCase()}`} />
        ))}
      </div>

      <div className="space-y-4">
        {incidents.map(inc => (
          <Card key={inc.id} className={`bg-white border-l-4 ${severityBorder(inc.severity)}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={severityColor(inc.severity)}>{inc.severity}</Badge>
                    <Badge className={statusColor(inc.status)}>{inc.status}</Badge>
                    <span className="text-sm font-semibold text-gray-800">{inc.title}</span>
                  </div>
                  <p className="text-sm text-gray-600">{inc.description}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                    <span>Site: {inc.asset_name}</span>
                    <span>Assigned: {inc.assigned_to}</span>
                    <span>Created: {fmtDate(inc.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              {inc.timeline && (
                <div className="mt-3 pl-4 border-l-2 border-gray-200 space-y-2">
                  {inc.timeline.map((t, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full -ml-[21px] ${t.status === 'Closed' ? 'bg-green-500' : t.status === inc.status ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                      <span className="text-xs text-gray-500 w-36">{fmtDate(t.timestamp)}</span>
                      <span className="text-xs text-gray-700">{t.event}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Mitigation Actions */}
              {inc.mitigation_actions && inc.mitigation_actions.length > 0 && (
                <div className="mt-3 bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-600 mb-2">Mitigation Actions</p>
                  {inc.mitigation_actions.map((m, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs text-gray-600">
                      <Badge className={m.status === 'Complete' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'} variant="outline">{m.status}</Badge>
                      <span>{m.action}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Feature Flags */}
              {inc.feature_flags && (
                <div className="flex gap-2 mt-3">
                  {inc.feature_flags.lockdown_mode && <Badge className="bg-red-100 text-red-700 text-xs">Lockdown Mode</Badge>}
                  {inc.feature_flags.command_throttling && <Badge className="bg-amber-100 text-amber-700 text-xs">Command Throttling</Badge>}
                  {inc.feature_flags.operator_approval_gate && <Badge className="bg-blue-100 text-blue-700 text-xs">Approval Gate</Badge>}
                </div>
              )}

              {/* Status Update Buttons */}
              {inc.status !== 'Closed' && (
                <div className="flex gap-2 mt-4">
                  {inc.status === 'Detected' && <Button size="sm" variant="outline" onClick={() => handleStatusUpdate(inc.id, 'Triaging')} data-testid={`triage-${inc.id}`}>Start Triage</Button>}
                  {inc.status === 'Triaging' && <Button size="sm" variant="outline" onClick={() => handleStatusUpdate(inc.id, 'Contained')} data-testid={`contain-${inc.id}`}>Mark Contained</Button>}
                  {inc.status === 'Contained' && <Button size="sm" variant="outline" onClick={() => handleStatusUpdate(inc.id, 'Remediating')} data-testid={`remediate-${inc.id}`}>Start Remediation</Button>}
                  {inc.status === 'Remediating' && <Button size="sm" variant="outline" onClick={() => handleStatusUpdate(inc.id, 'Closed')} data-testid={`close-${inc.id}`}>Close Incident</Button>}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {incidents.length === 0 && <p className="text-center text-gray-400 py-8">No incidents recorded.</p>}
      </div>
    </div>
  );
};

// ---- Compliance Tab ----
const ComplianceTab = ({ compliance }) => {
  if (!compliance) return <p className="text-center text-gray-400 py-8">No compliance data available.</p>;
  return (
    <div className="space-y-6" data-testid="compliance-content">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Overall Score" value={`${compliance.overall_score}%`} icon={Shield} color={compliance.overall_score > 80 ? 'green' : compliance.overall_score > 60 ? 'amber' : 'red'} testId="comp-overall" />
        <StatCard title="Devices Audited" value={compliance.total_devices_audited} icon={Server} color="gray" testId="comp-devices" />
        <StatCard title="Open Findings" value={compliance.open_findings} icon={AlertTriangle} color="amber" testId="comp-findings" />
        <StatCard title="Standards Tracked" value={compliance.standards?.length || 0} icon={Clipboard} color="blue" testId="comp-standards" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {(compliance.standards || []).map(std => (
          <Card key={std.standard} className="bg-white border-gray-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold">{std.standard}</CardTitle>
                <Badge className={`${std.score > 80 ? 'bg-green-100 text-green-700' : std.score > 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'} text-sm`}>{std.score}%</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden mb-4">
                <div className={`h-full rounded-full ${std.score > 80 ? 'bg-green-500' : std.score > 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${std.score}%` }} />
              </div>
              {std.gaps && std.gaps.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-600">Compliance Gaps:</p>
                  {std.gaps.map((gap, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-xs p-2 bg-gray-50 rounded">
                      <Badge className={gap.priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>{gap.priority}</Badge>
                      <div>
                        <span className="font-medium text-gray-700">{gap.area}</span>
                        <p className="text-gray-500">{gap.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ---- Threat Map Tab ----
const ThreatMapTab = ({ threatMap, fmtDate, setActiveTab }) => {
  const [selectedSite, setSelectedSite] = useState(null);
  const [showAttackVectors, setShowAttackVectors] = useState(true);
  const [showLateral, setShowLateral] = useState(true);

  if (!threatMap || !threatMap.site_nodes?.length) return <p className="text-center text-gray-400 py-8">No threat map data. Generate test data first.</p>;

  const sites = threatMap.site_nodes;
  const attacks = threatMap.attack_vectors || [];
  const laterals = threatMap.lateral_movements || [];
  const summary = threatMap.summary || {};

  // Melbourne region bounds
  const lats = sites.map(s => s.latitude);
  const lons = sites.map(s => s.longitude);
  const pad = 0.04;
  const bounds = {
    minLat: Math.min(...lats) - pad, maxLat: Math.max(...lats) + pad,
    minLon: Math.min(...lons) - pad, maxLon: Math.max(...lons) + pad
  };

  const toXY = (lat, lon) => ({
    x: ((lon - bounds.minLon) / (bounds.maxLon - bounds.minLon)) * 100,
    y: ((bounds.maxLat - lat) / (bounds.maxLat - bounds.minLat)) * 100
  });

  const threatColor = (score) => score > 50 ? '#ef4444' : score > 20 ? '#f59e0b' : score > 0 ? '#3b82f6' : '#22c55e';
  const threatGlow = (score) => score > 50 ? 'rgba(239,68,68,0.4)' : score > 20 ? 'rgba(245,158,11,0.3)' : 'rgba(34,197,94,0.2)';

  const selected = selectedSite ? sites.find(s => s.asset_id === selectedSite) : null;

  return (
    <div className="space-y-6" data-testid="threat-map-content">
      {/* Summary Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard title="Total Sites" value={summary.total_sites} icon={MapPin} color="gray" testId="tm-total-sites" />
        <StatCard title="Under Threat" value={summary.sites_under_threat} icon={AlertTriangle} color="red" testId="tm-threatened" />
        <StatCard title="Attack Vectors" value={summary.active_attack_vectors} icon={Globe} color="orange" testId="tm-vectors" />
        <StatCard title="Lateral Movement" value={summary.lateral_movements} icon={Network} color="purple" testId="tm-lateral" />
        <StatCard title="Active Incidents" value={summary.total_incidents} icon={Clipboard} color="red" testId="tm-incidents" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2">
          <Card className="bg-white border-gray-200 overflow-hidden">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold">Cyber Threat Map — Melbourne Metro</CardTitle>
              <div className="flex gap-2">
                <Button variant={showAttackVectors ? 'default' : 'outline'} size="sm" onClick={() => setShowAttackVectors(!showAttackVectors)} className={showAttackVectors ? 'bg-red-600 hover:bg-red-700 text-white text-xs h-7' : 'text-xs h-7'} data-testid="toggle-attacks-btn">
                  <Globe className="w-3 h-3 mr-1" />Attacks
                </Button>
                <Button variant={showLateral ? 'default' : 'outline'} size="sm" onClick={() => setShowLateral(!showLateral)} className={showLateral ? 'bg-purple-600 hover:bg-purple-700 text-white text-xs h-7' : 'text-xs h-7'} data-testid="toggle-lateral-btn">
                  <Network className="w-3 h-3 mr-1" />Lateral
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-3">
              <div className="bg-[#0c1821] rounded-xl relative overflow-hidden" style={{ height: '520px' }}>
                <svg className="w-full h-full" viewBox="-5 -5 110 110" preserveAspectRatio="xMidYMid meet" data-testid="threat-map-svg">
                  <defs>
                    <radialGradient id="siteGlow-red"><stop offset="0%" stopColor="#ef4444" stopOpacity="0.6" /><stop offset="100%" stopColor="#ef4444" stopOpacity="0" /></radialGradient>
                    <radialGradient id="siteGlow-amber"><stop offset="0%" stopColor="#f59e0b" stopOpacity="0.5" /><stop offset="100%" stopColor="#f59e0b" stopOpacity="0" /></radialGradient>
                    <radialGradient id="siteGlow-green"><stop offset="0%" stopColor="#22c55e" stopOpacity="0.4" /><stop offset="100%" stopColor="#22c55e" stopOpacity="0" /></radialGradient>
                    <filter id="glow"><feGaussianBlur stdDeviation="1" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
                    <marker id="arrowRed" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto"><path d="M0,0 L6,2 L0,4" fill="#ef4444" opacity="0.7" /></marker>
                    <marker id="arrowPurple" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto"><path d="M0,0 L6,2 L0,4" fill="#a855f7" opacity="0.7" /></marker>
                  </defs>

                  {/* Background grid */}
                  <pattern id="tGrid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="#1e3a4f" strokeWidth="0.15" /></pattern>
                  <rect x="-5" y="-5" width="110" height="110" fill="url(#tGrid)" />

                  {/* Melbourne region outline */}
                  <ellipse cx="50" cy="50" rx="42" ry="38" fill="none" stroke="#1e4d6e" strokeWidth="0.3" strokeDasharray="2,2" opacity="0.5" />
                  <text x="50" y="96" fontSize="2.5" fill="#4a7a9b" textAnchor="middle" opacity="0.6">Melbourne Metropolitan Area</text>

                  {/* Attack vector arcs (from edge of map to target sites) */}
                  {showAttackVectors && attacks.map((av, i) => {
                    const target = toXY(av.target.lat, av.target.lon);
                    // Place origins at map edges based on direction
                    const origins = [
                      { x: -3, y: 15 },  // Europe - left
                      { x: 105, y: 10 }, // East Asia - right top
                      { x: -3, y: 85 },  // South America - left bottom
                      { x: 105, y: 50 }, // North America - right mid
                      { x: 90, y: -3 },  // SE Asia - top right
                    ];
                    const origin = origins[i % origins.length];
                    const midX = (origin.x + target.x) / 2;
                    const midY = Math.min(origin.y, target.y) - 15;
                    return (
                      <g key={av.id} className="animate-pulse" style={{ animationDuration: `${2 + i * 0.5}s` }}>
                        <path d={`M${origin.x},${origin.y} Q${midX},${midY} ${target.x},${target.y}`} fill="none" stroke="#ef4444" strokeWidth="0.4" strokeDasharray="1.5,1" opacity="0.6" markerEnd="url(#arrowRed)" />
                        <text x={origin.x + (origin.x < 50 ? 2 : -2)} y={origin.y + 2} fontSize="1.6" fill="#ef4444" textAnchor={origin.x < 50 ? 'start' : 'end'} opacity="0.8">{av.origin.label}</text>
                        <circle cx={origin.x} cy={origin.y} r="1.2" fill="#ef4444" opacity="0.5" />
                      </g>
                    );
                  })}

                  {/* Lateral movement connections (site to site) */}
                  {showLateral && laterals.map(lm => {
                    const src = toXY(lm.source.lat, lm.source.lon);
                    const tgt = toXY(lm.target.lat, lm.target.lon);
                    return (
                      <g key={lm.id}>
                        <line x1={src.x} y1={src.y} x2={tgt.x} y2={tgt.y} stroke="#a855f7" strokeWidth="0.35" strokeDasharray="1,0.8" opacity="0.6" markerEnd="url(#arrowPurple)" />
                        <text x={(src.x + tgt.x) / 2} y={(src.y + tgt.y) / 2 - 1} fontSize="1.2" fill="#a855f7" textAnchor="middle" opacity="0.7">{lm.protocol}</text>
                      </g>
                    );
                  })}

                  {/* Site nodes */}
                  {sites.map(site => {
                    const pos = toXY(site.latitude, site.longitude);
                    const color = threatColor(site.threat_score);
                    const isSelected = selectedSite === site.asset_id;
                    const glowId = site.threat_score > 50 ? 'siteGlow-red' : site.threat_score > 20 ? 'siteGlow-amber' : 'siteGlow-green';
                    return (
                      <g key={site.asset_id} onClick={() => setSelectedSite(isSelected ? null : site.asset_id)} className="cursor-pointer" data-testid={`tm-site-${site.asset_id}`}>
                        {/* Glow ring */}
                        <circle cx={pos.x} cy={pos.y} r={isSelected ? 5 : 3.5} fill={`url(#${glowId})`} />
                        {/* Pulse ring for threatened sites */}
                        {site.threat_score > 20 && <circle cx={pos.x} cy={pos.y} r="2.5" fill="none" stroke={color} strokeWidth="0.2" opacity="0.5"><animate attributeName="r" from="2.5" to="5" dur="2s" repeatCount="indefinite" /><animate attributeName="opacity" from="0.5" to="0" dur="2s" repeatCount="indefinite" /></circle>}
                        {/* Core dot */}
                        <circle cx={pos.x} cy={pos.y} r={isSelected ? 2.2 : 1.5} fill={color} stroke={isSelected ? '#fff' : 'none'} strokeWidth="0.4" filter="url(#glow)" />
                        {/* Threat score label */}
                        {site.threat_score > 0 && <text x={pos.x} y={pos.y - 2.8} fontSize="1.5" fill={color} textAnchor="middle" fontWeight="bold">{site.threat_score}</text>}
                        {/* Name label for selected */}
                        {isSelected && <text x={pos.x} y={pos.y + 3.8} fontSize="1.8" fill="#e2e8f0" textAnchor="middle" fontWeight="600">{site.name.replace('SodiumQ-', '')}</text>}
                      </g>
                    );
                  })}
                </svg>

                {/* Legend */}
                <div className="absolute bottom-3 left-3 bg-[#0c1821]/90 border border-[#1e4d6e] rounded-lg p-2.5 space-y-1.5">
                  <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider mb-1">Legend</p>
                  <div className="flex items-center gap-2 text-[10px] text-gray-300"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />Critical (50+)</div>
                  <div className="flex items-center gap-2 text-[10px] text-gray-300"><span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" />Warning (20-50)</div>
                  <div className="flex items-center gap-2 text-[10px] text-gray-300"><span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />Safe (0-20)</div>
                  {showAttackVectors && <div className="flex items-center gap-2 text-[10px] text-red-400"><span className="w-4 border-t border-dashed border-red-500 inline-block" />External Attack</div>}
                  {showLateral && <div className="flex items-center gap-2 text-[10px] text-purple-400"><span className="w-4 border-t border-dashed border-purple-500 inline-block" />Lateral Movement</div>}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Side Panel */}
        <div className="space-y-4">
          {selected ? (
            <>
              {/* Selected Site Detail */}
              <Card className={`bg-white border-l-4 ${selected.threat_score > 50 ? 'border-red-500' : selected.threat_score > 20 ? 'border-amber-500' : 'border-green-500'}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center justify-between">
                    {selected.name}
                    <Badge className={`${selected.threat_score > 50 ? 'bg-red-600' : selected.threat_score > 20 ? 'bg-amber-500' : 'bg-green-600'} text-white`}>Score: {selected.threat_score}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-gray-500">{selected.location}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-gray-50 rounded p-2 text-center"><p className="text-lg font-bold text-red-600">{selected.critical_count}</p><p className="text-[10px] text-gray-500">Critical</p></div>
                    <div className="bg-gray-50 rounded p-2 text-center"><p className="text-lg font-bold text-orange-500">{selected.high_count}</p><p className="text-[10px] text-gray-500">High</p></div>
                    <div className="bg-gray-50 rounded p-2 text-center"><p className="text-lg font-bold text-gray-700">{selected.device_count}</p><p className="text-[10px] text-gray-500">Devices</p></div>
                    <div className="bg-gray-50 rounded p-2 text-center"><p className="text-lg font-bold text-amber-600">{selected.outdated_firmware}</p><p className="text-[10px] text-gray-500">Outdated FW</p></div>
                  </div>
                  {selected.active_incidents > 0 && <Badge className="bg-red-100 text-red-700">{selected.active_incidents} Active Incident(s)</Badge>}

                  {/* Category Breakdown */}
                  {Object.keys(selected.category_breakdown || {}).length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-1">Alert Categories</p>
                      <div className="space-y-1">
                        {Object.entries(selected.category_breakdown).map(([cat, count]) => (
                          <div key={cat} className="flex items-center justify-between text-xs">
                            <span className="text-gray-600 truncate max-w-[140px]">{cat}</span>
                            <Badge variant="outline" className="text-xs">{count}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Network Topology Mini */}
                  {selected.devices && selected.devices.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-1">Network Topology</p>
                      <div className="bg-[#0c1821] rounded-lg p-3" style={{ height: '150px' }}>
                        <svg viewBox="0 0 100 60" className="w-full h-full">
                          {/* Central node */}
                          <circle cx="50" cy="30" r="4" fill="#10b981" opacity="0.8" />
                          <text x="50" y="31.5" fontSize="3" fill="#fff" textAnchor="middle">GW</text>
                          {/* Device nodes around center */}
                          {selected.devices.map((dev, i) => {
                            const angle = (i / selected.devices.length) * 2 * Math.PI - Math.PI / 2;
                            const radius = 22;
                            const dx = 50 + Math.cos(angle) * radius;
                            const dy = 30 + Math.sin(angle) * radius;
                            const nodeColor = dev.status === 'Warning' ? '#f59e0b' : '#3b82f6';
                            return (
                              <g key={i}>
                                <line x1="50" y1="30" x2={dx} y2={dy} stroke="#1e4d6e" strokeWidth="0.4" />
                                <circle cx={dx} cy={dy} r="3" fill={nodeColor} opacity="0.8" />
                                <text x={dx} y={dy + 0.8} fontSize="2" fill="#fff" textAnchor="middle">{dev.type.substring(0, 3)}</text>
                                <text x={dx} y={dy + 5.5} fontSize="1.5" fill="#94a3b8" textAnchor="middle">{dev.ip.split('.').slice(-1)}</text>
                              </g>
                            );
                          })}
                        </svg>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Attack Vector List */}
              <Card className="bg-white border-gray-200">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Active Attack Vectors ({attacks.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {attacks.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No active attack vectors.</p>}
                    {attacks.map(av => (
                      <div key={av.id} className="p-2 bg-red-50 rounded border-l-2 border-red-400 cursor-pointer hover:bg-red-100" onClick={() => setSelectedSite(av.target.asset_id)}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-800">{av.origin.label}</span>
                          <Badge className={severityColor(av.severity)} style={{ fontSize: '9px', padding: '1px 6px' }}>{av.severity}</Badge>
                        </div>
                        <p className="text-[10px] text-gray-500 mt-0.5">{av.attack_type} via {av.access_type} &rarr; {av.target.name}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Top Threatened Sites */}
              <Card className="bg-white border-gray-200">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Most Threatened Sites</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {sites.filter(s => s.threat_score > 0).sort((a, b) => b.threat_score - a.threat_score).slice(0, 8).map(site => (
                      <div key={site.asset_id} className="flex items-center justify-between py-1 px-2 rounded hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedSite(site.asset_id)}>
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: threatColor(site.threat_score) }} />
                          <span className="text-xs text-gray-700 truncate max-w-[120px]">{site.name.replace('SodiumQ-', '')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">{site.alert_count} alerts</span>
                          <span className={`text-xs font-bold ${site.threat_score > 50 ? 'text-red-600' : site.threat_score > 20 ? 'text-amber-600' : 'text-blue-600'}`}>{site.threat_score}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CyberSecurityPage;
